import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { StoredSettingsService } from './stored-settings.service';
import { CachedProjectService } from '../../shared/services/cached-project.service';
import { ModelTypeSettings } from '../../models/modelTypeSettings';
import { Project } from '../../models/project';
import { SettingTypes } from '../../shared/enums/settingTypes';
import { vpLowerBound, vpUpperBound } from '../project/chart-slice/chart-slice.component';

export interface DisplaySettings {
  min?: number | null;        // For IL/XL slices (always same when manually set)
  max?: number | null;        // For IL/XL slices (always same when manually set)
  minZ?: number | null;       // For depth slice
  maxZ?: number | null;       // For depth slice
  isAuto?: boolean;           // Whether auto mode is enabled
  isAutoSynced?: boolean;     // Only matters when isAuto=true: true = synced ranges for IL/XL, false = independent
  palette: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class DisplaySettingsManager {
  private readonly settingsKey = "page";
  
  constructor(
    private storedSettingsService: StoredSettingsService,
    private projectService: CachedProjectService
  ) {}

  /**
   * Resolves settings in priority order: URL → Storage → Project Defaults → Hard Defaults
   */
  async resolveSettings(
    urlParams: Params,
    modelTypeId: string,
    jobId: string,
    projectData: Project,
    processing?: string[]
  ): Promise<DisplaySettings> {
    // 1. Check URL first (explicit user choice in this session)
    const urlSettings = this.extractFromUrl(urlParams, modelTypeId);
    if (this.hasExplicitRanges(urlSettings)) {
      return {
        ...urlSettings,
        isAutoSynced: true,
        palette: urlSettings.palette || this.getStoredPalette(modelTypeId, jobId) || this.getDefaultPalette(modelTypeId, processing)
      };
    }

    /**
     * The cleaning and spreading of UrlSettings is applied for a specific reason
     * the above scenario assumes that almost all [min + max + minZ + maxZ] have been provided in the urlParams
     * however due to the decoupling of depthSlice from being shown even, these values may not be defined while still containing other values
     * for that scenario, we first clean the params to only have values that actually have something instead of null
     * then we simply overwrite any other values with those values while the values not in the urlSettings remain unaffected
     * spreading has been applied to both, storedSettings and projectDefault scenarios
     */

    // get only settings that are not null
    const cleanedUrlSettings = Object.fromEntries(
      Object.entries(urlSettings).filter(([_, value]) => value !== null)
    );

    // 2. Check storage (user's saved preferences)
    const storedSettings = this.loadFromStorage(modelTypeId, jobId);
    if (storedSettings) {
      return {...storedSettings, ...cleanedUrlSettings};
    }

    // 3. Check project defaults for this model type - ONLY if no stored settings exist
    if (projectData) {
      const projectDefaults = this.getProjectDefaults(projectData, modelTypeId);
      if (projectDefaults) {
        return {
          ...projectDefaults,
          palette: this.getStoredPalette(modelTypeId, jobId) || this.getDefaultPalette(modelTypeId, processing),
          ...cleanedUrlSettings
        };
      }
    }

    // 4. Return hard defaults (will trigger API fetch)
    return {
      min: null,
      max: null,
      minZ: null,
      maxZ: null,
      isAuto: true,
      isAutoSynced: true,  // Default to synced auto
      palette: this.getStoredPalette(modelTypeId, jobId) || this.getDefaultPalette(modelTypeId, processing)
    };
  }

  private extractFromUrl(params: Params, modelTypeId: string): Partial<DisplaySettings> {
    const hasRanges = params['minVal'] || params['maxVal'] || 
                      params['minValZ'] || params['maxValZ'];
    
    if (!hasRanges) {
      return { isAuto: true };
    }

    let min = this.parseNumber(params['minVal']);
    let max = this.parseNumber(params['maxVal']);
    let minZ = this.parseNumber(params['minValZ']);
    let maxZ = this.parseNumber(params['maxValZ']);

    // Apply Vp bounds
    if (modelTypeId === "Vp") {
      min = this.clamp(min, vpLowerBound, vpUpperBound);
      max = this.clamp(max, vpLowerBound, vpUpperBound);
      minZ = this.clamp(minZ, vpLowerBound, vpUpperBound);
      maxZ = this.clamp(maxZ, vpLowerBound, vpUpperBound);
    }

    return {
      min,
      max,
      minZ,
      maxZ,
      isAuto: false,
      palette: null // Will be resolved separately
    };
  }

  private hasExplicitRanges(settings: Partial<DisplaySettings>): boolean {
    return settings.min != null && settings.max != null &&
           settings.minZ != null && settings.maxZ != null;
  }

  private loadFromStorage(modelTypeId: string, jobId: string): DisplaySettings | null {
    const settings = this.storedSettingsService.getSettings<ModelTypeSettings>(
      this.settingsKey,
      modelTypeId + jobId,
      SettingTypes.ModelType
    );

    if (!settings || !settings.ranges) {
      return null;
    }

    const ranges = settings.ranges;
    const sanitizedNums = [
      ranges.minValue, 
      ranges.maxValue, 
      ranges.minValueZ, 
      ranges.maxValueZ
    ].map(num => (num != null && !isNaN(num)) ? num : null);

    // Extract processing from stored settings to determine correct palette
    const processing = ranges.slice_processing || [];

    return {
      min: sanitizedNums[0],
      max: sanitizedNums[1],
      minZ: sanitizedNums[2],
      maxZ: sanitizedNums[3],
      isAuto: settings.velocityAuto ?? true,
      isAutoSynced: settings.velocityAutoSynced ?? true,
      palette: settings.palette || this.getDefaultPalette(modelTypeId, processing)
    };
  }

  private getProjectDefaults(project: Project, modelTypeId: string): DisplaySettings | null {

    if (!project.displayVariables || !project.displayVariables[modelTypeId]) {
      return null;
    }

    const displayVar = project.displayVariables[modelTypeId];
    
    // Check for null values (not set)
    if (displayVar.min === null || displayVar.max === null) {
      return null;
    }

    // For zero-centered types, verify the values are symmetric (min = -max)
    const isZeroCentered = modelTypeId !== 'Vp' && modelTypeId.trim() !== '';
    
    if (isZeroCentered) {
      // Verify it's symmetric (zero-centered)
      const isSymmetric = Math.abs(displayVar.min + displayVar.max) < 0.0001;
      if (!isSymmetric) {
        // If not symmetric, don't use it as a default for zero-centered types
        return null;
      }
    }

    return {
      min: displayVar.min,
      max: displayVar.max,
      minZ: displayVar.min,
      maxZ: displayVar.max,
      isAuto: false,
      isAutoSynced: false,
      palette: null // Will be resolved separately
    };
  }

  private getDefaultPalette(modelTypeId: string, processing: string[]): string {
    if (!modelTypeId) return "SEGRefl2";
    
    if (modelTypeId.includes("Gradient") || 
        modelTypeId.includes("Update") || 
        modelTypeId.includes("Error")) {
      return "red_white_blue";
    }
    
    if (modelTypeId.includes("vdiff") || modelTypeId.includes("RTM")) {
      return "greyscale";
    }
    
    if (modelTypeId === "Vp" || modelTypeId === "PreMergeVp" || modelTypeId === "Vs") {
      return "SEGRefl2";
    }

    if (processing?.includes("_zero_centred_params_process")) {
      return "greyscale";
    }
    
    return "SEGRefl2";
  }

  private getStoredPalette(modelTypeId: string, jobId: string): string | null {
    const settings = this.storedSettingsService.getSettings<ModelTypeSettings>(
      this.settingsKey,
      modelTypeId + jobId,
      SettingTypes.ModelType
    );
    return settings?.palette || null;
  }

  persistSettings(
    modelTypeId: string,
    jobId: string,
    projectId: string,
    displaySettings: DisplaySettings,
    processing: string[],
    isUpdatingModal?: boolean
  ): void {
    if (!modelTypeId || !jobId) return;

    const typeSettings: ModelTypeSettings = {
      id: modelTypeId + jobId,
      modelId: modelTypeId,
      projectId: projectId,
      iteration: null,
      palette: displaySettings.palette,
      ranges: {
        minValue: displaySettings.min,
        maxValue: displaySettings.max,
        minValueZ: displaySettings.minZ,
        maxValueZ: displaySettings.maxZ,
        slice_processing: processing,
      },
      velocityAuto: displaySettings.isAuto,
      velocityAutoSynced: displaySettings.isAutoSynced,
    };

    this.storedSettingsService.setSettings<ModelTypeSettings>(
      this.settingsKey,
      typeSettings,
      SettingTypes.ModelType,
      isUpdatingModal
    );
  }

  private parseNumber(val: any): number | null {
    if (val == null || val === '') return null;
    const num = Number(val);
    return isNaN(num) ? null : num;
  }

  private clamp(val: number | null, min: number, max: number): number | null {
    if (val == null) return null;
    return Math.max(min, Math.min(max, val));
  }
}
